import Options, { Option } from '@/hexapla-core/monads/option'

export interface Result<T, E> {
  ok: () => Option<T>
  err: () => Option<E>
  isOk: () => boolean
  isErr: () => boolean
  contains: (val: T) => boolean
  containsErr: (err: E) => boolean
  unwrap: () => T | undefined
  unwrapOr: (def: T) => T
  unwrapErr: () => E | undefined
  expect: (msg: string) => T | never
  next: (nextFunc: { (data: T): Result<T, E> }) => Result<T, E>
}

interface Ok<T> extends Result<T, unknown> {
  data: T
}

interface Err<E> extends Result<unknown, E> {
  error: E
}

export interface Results {
  newOk: <T>(data: T) => Result<T, unknown>
  newError: <E>(error: E) => Result<unknown, E>
}

function isOk<T, E>(this: Result<T, E>): boolean {
  return !!(this as Ok<T>)
}

function isErr<T, E>(this: Result<T, E>): boolean {
  return !!(this as Err<E>)
}

function contains<T, E>(this: Result<T, E>, val: T): boolean {
  return this.isOk() && (this as Ok<T>).data === val
}

function containsErr<T, E>(this: Result<T, E>, err: E): boolean {
  return this.isErr() && (this as Err<E>).error === err
}

function ok<T, E>(this: Result<T, E>): Option<T | false> {
  if (this.isOk()) return Options.some<T>(this.unwrap()!)
  return Options.none()
}

function err<T, E>(this: Result<T, E>): Option<E | false> {
  if (this.isErr()) return Options.some<E>(this.unwrapErr()!)
  return Options.none()
}

function unwrap<T, E>(this: Result<T, E>): T | undefined {
  if (this.isOk()) return (<Ok<T>>this).data
  return undefined
}

function unwrapOr<T, E>(this: Result<T, E>, def: T): T {
  if (this.isOk()) return (<Ok<T>>this).data
  return def
}

function unwrapErr<T, E>(this: Result<T, E>): E | undefined {
  if (this.isErr()) return (<Err<E>>this).error
  return undefined
}

function expect<T>(this: Result<T, unknown>, msg: string): T | never {
  if (this.isOk()) return this.unwrap()!

  throw new Error(msg)
}

function next<T, E>(this: Result<T, E>, nextFunc: { (data: T): Result<T, E> }): Result<T, E> {
  if(this.isErr()) return this

  return nextFunc(this.unwrap()!)
}

const newError = <E>(error: E): Result<unknown, E> => {
  return <Err<E>>{
    error,
    isOk,
    isErr,
    contains,
    containsErr,
    ok,
    err,
    unwrap,
    unwrapOr,
    unwrapErr,
    expect,
    next
  }
}

const newOk = <T>(data: T): Result<T, unknown> => {
  return <Ok<T>>{
    data,
    isOk,
    isErr,
    contains,
    containsErr,
    ok,
    err,
    unwrap,
    unwrapOr,
    unwrapErr,
    expect,
    next
  }
}

export default <Results>{ newOk, newError }
